import * as Switch from '@studio/design-system/switch';
import { useEffect, useState } from 'react';
import { useAuth0 } from '#~/auth/index.js';
import { config } from '#~/config/config.js';
import { nats } from '#~/nats/index.js';
import type { User } from './types.js';
import T from '@studio/design-system/themes';
import { AgentRegistryCreateInvite } from '@tryiris-ai/mailroom-helper/agentRegistry';
import { AgentRegistryMembershipRole } from '@tryiris-ai/common.protojs/api/v1/agent_registry';
import { usePrompt } from '@studio/design-system/prompt';
import { StudioError } from '#~/error/index.js';
import { Vendor } from '@studio/design-system/themes/vendor.js';
import { toHexFromId } from '#~/api/utils.js';
import { Icon } from '@studio/design-system/icon';
import { getCurrentVendor } from '@studio/design-system/themes/vendor.js';

export const SupportEmails: Record<Vendor, string> = {
  ptzoptics: 'hive-support@tryiris.ai',
  iris: 'iris-support@tryiris.ai'
};

export const InviteSupport = ({
  organizationId,
  roomId,
  supportUser,
  onRevoke
}: {
  organizationId: Uint8Array;
  roomId: Uint8Array;
  supportUser?: User | undefined;
  onRevoke?: (user: User) => void;
}) => {
  const [isEnabled, setIsEnabled] = useState(supportUser ? true : false);

  const [user, setUser] = useState(supportUser);

  useEffect(() => {
    if (supportUser) {
      setIsEnabled(true);
      setUser(supportUser);
    } else {
      setIsEnabled(false);
    }
  }, [setIsEnabled, setUser, supportUser]);

  const { getAccessTokenSilently } = useAuth0();

  const prompt = usePrompt();
  const domain = new URL(config.frontendUrl).hostname;

  const handleInviteSupport = async () => {
    try {
      const token = await getAccessTokenSilently();

      const invite = await AgentRegistryCreateInvite.request(nats.cloudConnection(), {
        token,
        organizationId,
        roomId,
        inviteeEmail: SupportEmails[getCurrentVendor()],
        role: AgentRegistryMembershipRole.AGENT_REGISTRY_MEMBERSHIP_ROLE_ADMIN,
        domain,
        membershipExpiresAt: Math.floor((Date.now() + 24 * 60 * 60 * 1000) / 1000) // +24 hours in seconds
      });

      setUser({
        id: toHexFromId(invite.id)!,
        email: invite.inviteeEmail!,
        expiration: invite.membershipExpiresAt ? new Date(invite.membershipExpiresAt) : undefined,
        permission: 'admin',
        status: 'pending'
      });
    } catch (error) {
      console.error('Error inviting support:', error);
      setIsEnabled(false);
      throw new StudioError(`Error enabling support access`);
    }

    setIsEnabled(true);
  };

  const handleRevokeSupport = async () => {
    if (!user) {
      console.error('No invite found');
      return;
    }

    if (onRevoke) {
      onRevoke(user);
      setUser(undefined);
      setIsEnabled(false);
    }
  };

  const onCheck = async (checked: boolean) => {
    if (checked) {
      prompt.confirm({
        title: 'Invite Support',
        message:
          'This will allow a support team member to join your Studio as an admin guest to help troubleshoot any issues. Do you want to continue?',
        onConfirm: (answer) => {
          if (answer) {
            handleInviteSupport();
          }
        }
      });
    } else {
      prompt.confirm({
        title: 'Revoke Support Access',
        message: 'This will revoke the support access to your Studio. Do you want to continue?',
        onConfirm: (answer) => {
          if (answer) {
            handleRevokeSupport();
          }
        }
      });
    }
  };

  return null;

  return (
    <div className='flex flex-col py-4'>
      <div className='absolute left-[0] right-[0] mb-32 mt-[-32px] w-full border-t border-base-300'></div>
      <div className=''>
        <Switch.Container
          data-theme={T.getTheme('dark')}
          className='w-full !from-transparent !to-transparent !pl-0'
          label={
            <div className='flex w-full items-center justify-between gap-10'>
              <div className='flex items-center gap-10'>
                <Icon
                  className='text-l bg-neutral-300 p-1 rounded-full'
                  icon={'headphone'}
                  style={{
                    color: '#416CFD',
                    backgroundColor: '#E7E9EE',
                    padding: '0.75rem'
                  }}
                />
                <div className='gap-1 flex flex-col text-left'>
                  <span className='text-lg font-medium'>Invite Support</span>
                  <span className='!text-neutral-400 !text-sm'>
                    Enable for remote troubleshooting and support.
                  </span>
                </div>
              </div>
            </div>
          }
        >
          <Switch.Archetype
            className='w-[45px] text-white'
            checked={isEnabled}
            disabled={false}
            onCheckedChange={onCheck}
          />
        </Switch.Container>
      </div>
    </div>
  );
};
